/**
 * I-228 (bodega T14·13) — UNA sola forma de traer «todo» de una lista del servidor.
 *
 * Antes, cada pantalla pedía una página fija de 100 (o de 50) y se quedaba con la primera página: el proyecto 101, la
 * escrituración 101 o la tarea 101 no aparecían en el selector, y nadie lo decía. Aquí se recorren las páginas
 * de 200 (el tope del motor del servidor, `helpers/pagination.ts` MAX_PER_PAGE) siguiendo `has_next`; nunca se
 * pide «todo de golpe». Si se llega al tope de seguridad, `truncada` sale en `true`, `pagination.has_next` sigue en
 * `true` (los avisos «no caben» que ya existen lo siguen diciendo) y se avisa con un toast: ninguna lista se corta
 * en silencio. Se quitan filas repetidas por `id`: con LIMIT/OFFSET sin orden estable el servidor puede repetir una
 * fila entre páginas. Este motor SUSTITUYE a `traerTodasLasPaginas` de I-204 (api/client.ts), que se apagó en el mismo commit.
 */
import toast from 'react-hot-toast';
import type { Pagination } from '@/types';

export const POR_PAGINA = 200;
export const TOPE_DE_PAGINAS = 25; // 25 × 200 = 5,000 renglones: más que eso es una lista que necesita filtro, no selector

export type ConTruncada<R> = R & { truncada: boolean };

type Pagina<T> = { data: T[]; pagination?: Pagination };

/** Recorre las páginas de `POR_PAGINA` y devuelve TODOS los renglones con la forma de la primera respuesta. */
export async function traerTodas<T, R extends Pagina<T>>(
  fetchPagina: (page: number, per_page: number) => Promise<R>,
  opciones: { tope?: number } = {},
): Promise<ConTruncada<R>> {
  const tope = opciones.tope ?? TOPE_DE_PAGINAS;
  const primera = await fetchPagina(1, POR_PAGINA);
  const filas: T[] = [...(primera.data ?? [])];
  let pag = primera.pagination;
  let page = 1;
  while (pag?.has_next && page < tope) {
    page += 1;
    const sig = await fetchPagina(page, POR_PAGINA);
    filas.push(...(sig.data ?? []));
    pag = sig.pagination ?? { ...pag, has_next: false };
  }
  const truncada = Boolean(pag?.has_next);
  // Sin orden estable en el servidor una fila puede aparecer en dos páginas: se queda la primera por `id`.
  const vistos = new Set<unknown>();
  const data = filas.filter((f) => {
    const id = (f as { id?: unknown } | null)?.id;
    if (id === undefined || id === null) return true;
    if (vistos.has(id)) return false;
    vistos.add(id);
    return true;
  });
  if (truncada) {
    // Ninguna lista se corta en silencio: se dice (mismo id ⇒ no se apilan).
    toast(`La lista es muy grande: se muestran los primeros ${data.length.toLocaleString('es-MX')}. Afina el filtro para ver el resto.`, { id: 'lista-truncada' });
  }
  const total = pag?.total ?? data.length;
  return {
    ...primera,
    data,
    pagination: { page: 1, per_page: POR_PAGINA, total: Math.max(total, data.length),
      total_pages: 1, has_next: truncada, has_prev: false },
    truncada,
  };
}
